import { useEffect, useMemo, useRef } from 'react';
import { useSelection } from '../contexts/SelectionContext';
import { useViewSync } from '../contexts/ViewSyncContext';
import { lonLatDistanceM, projectOnLine, xFromFrac } from '../lib/view-sync';

/** Cursor further than this from the section line is not "on" the section. */
const SECTION_HOVER_TOLERANCE_M = 40;
/** Camera echoes below this (metres) are ignored so map <-> 3D never ping-pong. */
const CAMERA_EPS_M = 1;

/**
 * MapView side of the view sync (docs/TODO.md 3.7): publishes the map cursor as a hover,
 * mirrors the current section line into the shared context, and keeps the map centre and
 * the 3D camera target in step while "Sync camera" is on.
 */
export function useViewSyncMap({ mapRefHolder, mouseCoord, viewport, inversionModels = [], showSectionView, sectionModelId }) {
  const { selection } = useSelection();
  const { hovered, setHover, clearHover, sectionLine, setSectionLine, syncCamera, setSyncCamera, camera, publishCamera } = useViewSync();
  const sectionLineRef = useRef(null);
  sectionLineRef.current = sectionLine;
  const lastCamera = useRef(null); // last lon/lat exchanged, either direction

  // Section line: the open section's model line, else the selected survey line
  const wanted = useMemo(() => {
    if (showSectionView) {
      const entry = inversionModels.find((m) => m.id === sectionModelId) || null;
      if (entry?.line?.length >= 2) {
        return { coordinates: entry.line, modelId: entry.id, name: entry.model?.name || entry.name || 'Section', xRange: entry.model?.xRange || null };
      }
    }
    if (selection?.type === 'line' && selection.coordinates?.length >= 2) {
      return { coordinates: selection.coordinates, modelId: null, name: selection.name || 'Survey line', xRange: null };
    }
    return null;
  }, [showSectionView, inversionModels, sectionModelId, selection]);
  useEffect(() => { setSectionLine(wanted); }, [wanted, setSectionLine]);

  // Map cursor -> hover for the other views (throttled in the provider)
  useEffect(() => {
    if (!mouseCoord) return;
    const lonLat = [mouseCoord.lon, mouseCoord.lat];
    const sl = sectionLineRef.current;
    const proj = sl ? projectOnLine(sl.coordinates, lonLat) : null;
    const onLine = proj && proj.offset <= SECTION_HOVER_TOLERANCE_M;
    setHover({
      source: 'map', modelId: onLine ? sl.modelId : null,
      x: onLine ? xFromFrac(sl, proj.frac) : null, depth: null, lonLat,
    });
  }, [mouseCoord, setHover]);
  useEffect(() => () => clearHover(), [clearHover]);

  // Camera: map centre -> 3D
  useEffect(() => {
    if (!syncCamera || !viewport || typeof viewport.longitude !== 'number') return;
    const lonLat = [viewport.longitude, viewport.latitude];
    if (lonLatDistanceM(lonLat, lastCamera.current) < CAMERA_EPS_M) return;
    lastCamera.current = lonLat;
    publishCamera({ lonLat, source: 'map' });
  }, [syncCamera, viewport, publishCamera]);

  // Camera: 3D target -> map centre
  useEffect(() => {
    if (!syncCamera || !camera || camera.source === 'map') return;
    if (lonLatDistanceM(camera.lonLat, lastCamera.current) < CAMERA_EPS_M) return;
    const map = mapRefHolder?.current?.getMap?.();
    if (!map) return;
    lastCamera.current = camera.lonLat;
    map.easeTo({ center: camera.lonLat, duration: 0 });
  }, [syncCamera, camera, mapRefHolder]);

  return { hovered, sectionLine, syncCamera, setSyncCamera };
}
